import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  LinkedinOutlined,
  TeamOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Link } = Typography;

const ConsultantDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const brandColor = '#470172';
  const [loading, setLoading] = useState(true);
  const [consultantData, setConsultantData] = useState(null);
  const [profilePictureError, setProfilePictureError] = useState(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL || '';

  // Default fallback images
  const defaultProfilePicture = 'https://ui-avatars.com/api/?name=Consultant&background=470172&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchConsultantDetails();
  }, [id]);

  const fetchConsultantDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/consultants/${id}`);
      setConsultantData(response.data);
    } catch (error) {
      message.error('Failed to load consultant details');
      console.error('Error fetching consultant:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'approved' || status === 'active') return 'success';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  const profilePictureUrl = consultantData?.profilePicture ? getImageUrl(consultantData.profilePicture) : defaultProfilePicture;
  const bannerUrl = defaultBanner;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!consultantData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No consultant data available.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100%',
        padding: "20px 10px",
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Back Button */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 20px auto',
          paddingLeft: '10px'
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: brandColor,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = brandColor;
              e.currentTarget.style.color = brandColor;
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(71, 1, 114, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d9d9d9';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Back
          </Button>
        </div>

        {/* Main Container */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          padding: '0 10px'
        }}>
          {/* Banner Image */}
          <div style={{
            width: '100%',
            height: '250px',
            backgroundImage: `url(${bannerUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '10px',
            marginBottom: '24px',
            position: 'relative',
            border: '1px solid #e8e8e8'
          }}
          />

          {/* Profile Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -100,
              position: 'relative',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {profilePictureError || !consultantData?.profilePicture ? (
                    <Avatar
                      size={130}
                      style={{
                        backgroundColor: brandColor,
                        fontSize: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                      icon={<UserOutlined />}
                    >
                      {consultantData?.fullName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  ) : (
                    <img
                      src={profilePictureUrl}
                      alt="Consultant Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%'
                      }}
                      onError={() => {
                        console.error('Profile picture failed to load:', profilePictureUrl);
                        setProfilePictureError(true);
                      }}
                      onLoad={() => console.log('Profile picture loaded successfully')}
                    />
                  )}
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px'
                    }}
                  >
                    {consultantData.fullName || 'Consultant Name'}
                  </Title>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      Consultant
                    </Tag>
                    {consultantData.isVerified && (
                      <Tag
                        icon={<SafetyCertificateOutlined />}
                        color="success"
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        Verified
                      </Tag>
                    )}
                    {consultantData.status && (
                      <Tag
                        color={getStatusColor(consultantData.status)}
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {consultantData.status.charAt(0).toUpperCase() + consultantData.status.slice(1)}
                      </Tag>
                    )}
                  </Space>
                  {consultantData.linkedinProfile && (
                    <Link
                      href={consultantData.linkedinProfile}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: brandColor
                      }}
                    >
                      <LinkedinOutlined /> View LinkedIn Profile
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            
            {/* Left Column: Professional Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <TeamOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Professional Details
                  </Space>
                }
                bordered={false}
                style={{ height: 'auto' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    textAlign: 'left'
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-sans",
                    fontWeight: 400,
                    color: '#333',
                    textAlign: 'left'
                  }}
                >
                  {consultantData.yearsOfExperience && (
                    <Descriptions.Item label="Years of Experience" span={1}>
                      <Text style={{ fontSize: '17px', fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {consultantData.yearsOfExperience} years
                      </Text>
                    </Descriptions.Item>
                  )}

                  {consultantData.expertise && (
                    <Descriptions.Item label="Expertise" span={1}>
                      <Text style={{ fontSize: '17px', fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {consultantData.expertise}
                      </Text>
                    </Descriptions.Item>
                  )}

                  {consultantData.consultingFields && consultantData.consultingFields.length > 0 && (
                    <Descriptions.Item label="Consulting Fields" span={2}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {consultantData.consultingFields.map((field, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px'
                            }}
                          >
                            <span style={{
                              color: brandColor,
                              fontSize: '20px',
                              lineHeight: '24px',
                              flexShrink: 0,
                              marginTop: '-2px'
                            }}>•</span>
                            <span style={{
                              flex: 1,
                              fontSize: '17px',
                              fontFamily: "'Rubik', sans-serif",
                              fontWeight: 400,
                              color: '#333',
                              wordBreak: 'normal',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal'
                            }}>
                              {field}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {consultantData.description && (
                <Card
                  title={
                    <Space style={{
                      fontSize: '20px',
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      color: brandColor
                    }}>
                      About
                    </Space>
                  }
                  bordered={false}
                  style={{ marginTop: 24 }}
                >
                  <Text
                    style={{
                      display: 'block',
                      lineHeight: 1.8,
                      fontSize: '16px',
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      color: '#333'
                    }}
                  >
                    {consultantData.description}
                  </Text>
                </Card>
              )}
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{ height: 'auto' }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  {/* Email */}
                  {consultantData.email && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <MailOutlined style={{ fontSize: 22, color: brandColor }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            Email
                          </Text>
                          <Link href={`mailto:${consultantData.email}`} style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                            {consultantData.email}
                          </Link>
                        </div>
                      </div>
                      <Divider style={{ margin: '3px 0' }} />
                    </>
                  )}

                  {/* Phone */}
                  {consultantData.phone && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <PhoneOutlined style={{ fontSize: 22, color: brandColor }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            Phone
                          </Text>
                          <Link href={`tel:${consultantData.phone}`} style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                            {consultantData.phone}
                          </Link>
                        </div>
                      </div>
                      <Divider style={{ margin: '3px 0' }} />
                    </>
                  )}

                  {/* LinkedIn */}
                  {consultantData.linkedinProfile && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <LinkedinOutlined style={{ fontSize: 22, color: brandColor }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            LinkedIn
                          </Text>
                          <Link href={consultantData.linkedinProfile} target="_blank" style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                            View Profile
                          </Link>
                        </div>
                      </div>
                      <Divider style={{ margin: '3px 0' }} />
                    </>
                  )}
                  
                  {/* Location */}
                  {(consultantData.city || consultantData.state) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: '#F5F5F5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <EnvironmentOutlined style={{ fontSize: 22, color: brandColor }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                          Location
                        </Text>
                        <Text style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block' }}>
                          {consultantData.city && consultantData.state
                            ? `${consultantData.city}, ${consultantData.state}`
                            : consultantData.city || consultantData.state}
                        </Text>
                        {consultantData.country && (
                          <Text style={{ display: 'block', fontSize: 15, color: '#999', fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            {consultantData.country}
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ConsultantDetails;
